import { useEffect, useRef, useState } from 'react';
import SectionCard from '../components/SectionCard.jsx';
import { Pair, Mono, References } from './dataReferenceParts.jsx';

/**
 * One source of the data reference: what it is (endpoint, license, refresh
 * cadence, file layout), its fields (type, nullable, description, example,
 * source, since), a complete example record, for the CSV usage reports the
 * position of every column, and a Qlik Sense load script. Everything comes
 * from GET /data-reference (data-catalog.mjs on the server); nothing here is
 * written per source.
 */
export default function SourceCard({ source: s }) {
  return (
    <SectionCard
      as="article"
      id={s.anchor}
      headingLevel={3}
      title={s.label}
      className="scroll-mt-16 lg:scroll-mt-4"
      bodyClassName="space-y-4"
      action={(
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1 font-normal">
          <span className="badge-slate font-mono">{s.key}</span>
          {s.license?.required && <span className="badge-amber">{s.license.label}</span>}
          <a href={`#${s.anchor}`} className="inline-flex min-h-10 min-w-10 items-center justify-center text-xs text-ipc-muted underline hover:no-underline sm:min-h-0 sm:min-w-0" aria-label={`Link to ${s.label}`}>#</a>
        </span>
      )}
    >
      <p className="text-sm text-ipc-ink">{s.grain}</p>

      <dl className="grid grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
        <Pair term="S3 location"><Mono>{s.s3Uri}</Mono></Pair>
        <Pair term="Files">
          <ul className="space-y-0.5">{s.files.map((f) => <li key={f}><Mono>{f}</Mono></li>)}</ul>
        </Pair>
        {s.key !== 'reports' && <EndpointPair endpoints={s.endpoints} />}
        <Pair term="Produced">{s.production.label}. {s.production.description}</Pair>
        <Pair term="Date in file name">{s.production.dateInName}</Pair>
        <Pair term="Refresh cadence">{s.hint.cadence}</Pair>
        <Pair term="Schedule">{s.schedule}</Pair>
        {s.license && <Pair term="License">{s.license.text}</Pair>}
        {s.permissions && <Pair term="Permissions">{s.permissions.text}</Pair>}
        <Pair term="Format">
          {s.format}, compression: {s.compression.toLowerCase()}.{' '}
          {s.shape === 'object' ? 'One JSON object per file.' : s.shape === 'array' ? 'One JSON array of rows per file.' : ''}
        </Pair>
        <Pair term="_latest.json">
          {s.latest
            ? (s.tombstone
              ? 'Yes. Replaced by a tombstone when the source does not run.'
              : 'Yes. Keeps the last collected data when the source is turned off (no tombstone).')
            : 'No.'}
        </Pair>
        {s.dependsOn.length > 0 && (
          <Pair term="Depends on">
            {s.dependsOn.map((d, i) => (
              <span key={d.key}>
                {i > 0 && ', '}
                <a href={`#source-${d.key}`} className="text-ipc-light underline hover:no-underline">{d.label}</a>
              </span>
            ))}
            {' '}(collected in the same run)
          </Pair>
        )}
        {s.entity && <Pair term="Entity (field prefix)"><Mono>{s.entity}</Mono></Pair>}
        {s.primaryKey.length > 0 && <Pair term="Primary key"><Mono>{s.primaryKey.join(' + ')}</Mono></Pair>}
        {s.references.length > 0 && (
          <Pair term="Links to">
            <References refs={s.references} />
          </Pair>
        )}
      </dl>

      {s.fields.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-ipc-primary mb-1">Fields</h4>
          <FieldsLegend />
          <FieldsTable fields={s.fields} label={`${s.label} fields`} />
        </div>
      )}
      {s.fields.length > 0 && <SamplePanel sample={s.sample} note={s.sampleNote} label={s.label} />}
      {s.fields.length > 0 && <QlikPanel script={s.qlikScript} label={s.label} />}

      {s.subReports.map((r) => (
        <details key={r.id} className="min-w-0 rounded-md border border-ipc-border p-3">
          <summary className="cursor-pointer text-sm font-semibold text-ipc-primary">
            {r.name}{' '}
            <span className="font-normal text-ipc-muted">
              (<Mono>{`reports/${r.id}/`}</Mono>, {r.fields.length} columns)
            </span>
          </summary>
          <div className="mt-3 space-y-4">
            <p className="text-sm text-ipc-ink">
              Entity <Mono>{r.entity}</Mono>, one row per <Mono>{r.primaryKey.join(' + ')}</Mono>. Links to{' '}
              <References refs={r.references} />.
            </p>
            <dl className="grid grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
              <EndpointPair endpoints={r.endpoints} />
            </dl>
            <div>
              <h4 className="text-sm font-semibold text-ipc-primary mb-1">Columns</h4>
              <FieldsLegend />
              <FieldsTable fields={r.fields} label={`${r.name} columns`} />
            </div>
            <SamplePanel sample={r.sample} note={r.sampleNote} label={r.name} />
            <ColumnPositions columns={r.columns} label={r.name} />
            <QlikPanel script={r.qlikScript} label={r.name} />
          </div>
        </details>
      ))}

      <p className="text-xs text-ipc-muted">
        Properties Microsoft adds later are delivered too, named by the same rule.
        {s.docsUrl && (
          <>
            {' '}
            <a href={s.docsUrl} target="_blank" rel="noopener noreferrer" className="text-ipc-light underline hover:no-underline">
              Microsoft Graph documentation
            </a>
          </>
        )}
      </p>

      {s.caveats.length > 0 && (
        <div className="border-l-4 py-1 pl-4" style={{ borderLeftColor: 'var(--warn)' }} data-testid="source-caveats">
          <h4 className="mb-1 text-sm font-semibold text-ipc-ink">Caveats</h4>
          <ul className="list-disc pl-5 space-y-1 text-sm text-ipc-ink">
            {s.caveats.map((c) => <li key={c}>{c}</li>)}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}

const EXAMPLE_MAX = 80;

// The example as text: a string as it is (quotes would only add noise next to
// the Type column), anything else as compact JSON, cut short when long (the
// full value is in the title, and the whole record is shown below the table).
function exampleText(value) {
  const text = typeof value === 'string' ? (value === '' ? '""' : value) : JSON.stringify(value);
  return text.length > EXAMPLE_MAX ? { shown: `${text.slice(0, EXAMPLE_MAX - 3)}...`, full: text } : { shown: text, full: null };
}

// The tree of fields as the flat list of rows the table shows: a nested field
// follows its parent, one level deeper.
function flatten(fields, depth = 0) {
  return fields.flatMap((f) => [{ field: f, depth }, ...flatten(f.children ?? [], depth + 1)]);
}

/** One line under the Fields heading: what Since and Nullable mean. */
function FieldsLegend() {
  return (
    <p className="mb-2 text-xs text-ipc-muted">
      Since is the data schema version whose files first carry the field. A nested field is listed under its parent
      with its full path. Nullable fields can be missing or null in a row. Every example is synthetic.
    </p>
  );
}

/** The Microsoft Graph requests behind a table, as a definition row. */
function EndpointPair({ endpoints }) {
  if (!endpoints?.length) return null;
  return (
    <Pair term="Microsoft Graph">
      <ul className="space-y-1">
        {endpoints.map((e) => (
          <li key={e.endpoint}>
            <Mono>{`${e.method} ${e.url}`}</Mono>
            {e.note && <span className="block text-ipc-muted">{e.note}</span>}
          </li>
        ))}
      </ul>
    </Pair>
  );
}

/**
 * Field, Type, Nullable, Description, Example, Source, Since. Every row has
 * its own anchor (#source-<id>-field-<path>) so a column can be linked.
 */
export function FieldsTable({ fields, label }) {
  const rows = flatten(fields);
  return (
    <div className="relative overflow-x-auto border-t border-ipc-border">
      <table className="min-w-full text-sm" aria-label={label}>
        <thead>
          <tr className="text-left text-ipc-muted border-b border-ipc-border">
            <th scope="col" className="py-1.5 pr-4 font-medium">Field</th>
            <th scope="col" className="py-1.5 pr-4 font-medium">Type</th>
            <th scope="col" className="py-1.5 pr-4 font-medium">Nullable</th>
            <th scope="col" className="py-1.5 pr-4 font-medium">Description</th>
            <th scope="col" className="py-1.5 pr-4 font-medium">Example</th>
            <th scope="col" className="py-1.5 pr-4 font-medium">Source</th>
            <th scope="col" className="py-1.5 font-medium">Since</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ field: f, depth }) => <FieldRow key={f.anchor} field={f} depth={depth} />)}
        </tbody>
      </table>
    </div>
  );
}

function FieldRow({ field: f, depth }) {
  const example = exampleText(f.example);
  return (
    <tr id={f.anchor} className="border-b border-ipc-border align-top scroll-mt-16 lg:scroll-mt-4 target:bg-amber-50">
      <td className="py-1.5 pr-4 whitespace-nowrap" style={depth > 0 ? { paddingLeft: `${depth * 1.25}rem` } : undefined}>
        <a href={`#${f.anchor}`} className="hover:underline" aria-label={`Link to ${f.path}`}>
          <Mono>{depth > 0 ? f.path : f.name}</Mono>
        </a>
      </td>
      <td className="py-1.5 pr-4 whitespace-nowrap text-ipc-ink">{f.type}</td>
      <td className="py-1.5 pr-4 whitespace-nowrap text-ipc-ink">{f.nullable ? 'yes' : 'no'}</td>
      <td className="py-1.5 pr-4 text-ipc-ink min-w-[16rem]">
        {f.description}
        {f.notes && <span className="mt-1 block text-xs text-ipc-muted">{f.notes}</span>}
      </td>
      <td className="py-1.5 pr-4 text-ipc-ink min-w-[10rem] max-w-[18rem]">
        <code className="font-mono text-xs [overflow-wrap:anywhere]" title={example.full ?? undefined}>{example.shown}</code>
      </td>
      <td className="py-1.5 pr-4 text-ipc-ink min-w-[12rem]">
        {f.source.derivedFrom ? (
          <>
            <span className="block">Computed by onGuard</span>
            <span className="block text-xs text-ipc-muted">{f.source.derivedFrom}</span>
          </>
        ) : (
          <Mono>{f.source.property}</Mono>
        )}
        <span className="block text-xs text-ipc-muted [overflow-wrap:anywhere]">{f.source.apiVersion} {f.source.endpoint}</span>
      </td>
      <td className="py-1.5 whitespace-nowrap text-ipc-ink">v{f.sinceVersion}</td>
    </tr>
  );
}

/** Usage reports are CSV: the position of each column in the header row, 1 to n. */
function ColumnPositions({ columns, label }) {
  return (
    <section aria-label={`${label} column positions`} className="min-w-0">
      <h4 className="mb-1 text-sm font-semibold text-ipc-primary">Column positions</h4>
      <p className="mb-1 text-xs text-ipc-muted">
        A row of the CSV holds values only: read a column by its position, or by the header row, which carries the
        names below. Examples are the text as it appears in the file.
      </p>
      <div className="relative overflow-x-auto border-t border-ipc-border">
        <table className="min-w-full text-sm" aria-label={`${label} column positions`}>
          <thead>
            <tr className="text-left text-ipc-muted border-b border-ipc-border">
              <th scope="col" className="py-1.5 pr-4 font-medium">Position</th>
              <th scope="col" className="py-1.5 pr-4 font-medium">Column</th>
              <th scope="col" className="py-1.5 pr-4 font-medium">Type</th>
              <th scope="col" className="py-1.5 font-medium">Example</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((c) => (
              <tr key={c.position} className="border-b border-ipc-border align-top">
                <td className="py-1.5 pr-4 text-ipc-ink">{c.position}</td>
                <td className="py-1.5 pr-4 whitespace-nowrap"><Mono>{c.name}</Mono></td>
                <td className="py-1.5 pr-4 whitespace-nowrap text-ipc-ink">{c.type}</td>
                <td className="py-1.5 text-ipc-ink"><code className="font-mono text-xs [overflow-wrap:anywhere]">{c.example === '' ? '""' : c.example}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Copies text to the clipboard; the label flips to "Copied" for two seconds. */
export function CopyButton({ text, label }) {
  const [state, setState] = useState('idle');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    let next = 'copied';
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // No clipboard permission or an insecure context: say so, the reader
      // can still select the text below.
      next = 'failed';
    }
    setState(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 2000);
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy ${label}`}
      className="inline-flex min-h-10 items-center rounded border border-ipc-border bg-white px-2.5 text-xs font-medium text-ipc-ink hover:bg-ipc-surface sm:min-h-0 sm:py-1"
    >
      <span aria-live="polite">{state === 'copied' ? 'Copied' : state === 'failed' ? 'Select and copy' : 'Copy'}</span>
    </button>
  );
}

function SamplePanel({ sample, note, label }) {
  return (
    <section aria-label={`${label} sample`} className="min-w-0">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-ipc-ink">Example record</h4>
        <CopyButton text={sample} label={`${label} sample`} />
      </div>
      <p className="mb-1 text-xs text-ipc-muted">
        Every field with its example: synthetic values, never customer data. {note}
      </p>
      <pre className="max-h-96 overflow-auto rounded-md bg-slate-900 p-3 font-mono text-xs text-slate-100">{sample}</pre>
    </section>
  );
}

function QlikPanel({ script, label }) {
  return (
    <details className="min-w-0 rounded-md border border-ipc-border px-3 py-2">
      <summary className="cursor-pointer text-sm font-semibold text-ipc-primary">Qlik Sense load script</summary>
      <div className="mt-2 min-w-0">
        <div className="mb-1 flex justify-end">
          <CopyButton text={script} label={`${label} load script`} />
        </div>
        <pre className="max-h-96 overflow-auto rounded-md bg-slate-900 p-3 font-mono text-xs text-slate-100">{script}</pre>
      </div>
    </details>
  );
}
